"use client";

import { Search, Microscope, CircleCheck as CheckCircle, Globe } from 'lucide-react';
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Process() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: cardsRef,
    isVisible: cardsVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const steps = [{
    number: "01",
    title: "Discover",
    body: "Identify promising medical technologies worldwide.\nDiscover innovative medical devices and healthcare solutions from trusted global manufacturers.",
    duration: "\n",
    Icon: Search,
    rotation: "-rotate-1",
    hoverRotation: "md:hover:rotate-0"
  }, {
    number: "02",
    title: "Evaluate",
    body: "Understand product value and market potential.\n\n\nReview clinical applications, available evidence, product positioning, and market opportunities to support informed business decisions.",
    duration: "\n",
    Icon: Microscope,
    rotation: "rotate-[1deg]",
    hoverRotation: "md:hover:rotate-0"
  }, {
    number: "03",
    title: "Connect\xA0",
    body: "Create valuable business partnerships.\nFacilitate collaboration between manufacturers, distributors, and healthcare organizations.",
    duration: "\n",
    Icon: CheckCircle,
    rotation: "-rotate-1",
    hoverRotation: "md:hover:rotate-0"
  }, {
    number: "04",
    title: "Grow",
    body: "Expand into international markets.\nSupport business development through strategic partnerships and international market opportunities.",
    duration: "GLOBAL PARTNERSHIP",
    Icon: Globe,
    rotation: "rotate-[1deg]",
    hoverRotation: "md:hover:rotate-0"
  }];
  return <section data-section-id="1124" id="businessprocesstimeline" className="bg-background py-28 md:py-40 relative overflow-hidden" style={{
    borderTop: "1px solid var(--border)"
  }}>
      <div className="max-w-[1440px] mx-auto px-4 md:px-8">
        <div ref={headerRef} className={`transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="font-default text-xs uppercase tracking-wide text-primary mb-10 md:mb-16">§ OUR EVALUATION APPROACH</div>

          <h2 className="font-default text-foreground leading-tight mb-6 font-bold text-6xl">
            <span>Evaluating Innovation.&nbsp;</span>
            <span className="relative inline-block align-baseline">
              <span className="relative inline-block px-5 md:px-7 py-1 md:py-2 bg-primary text-white rounded-lg" style={{
              boxShadow: "var(--shadow-md)"
            }}>Creating Opportunity.</span>
            </span>
          </h2>

          <svg className="mb-8 text-primary" width="180" height="14" viewBox="0 0 180 14" fill="none" aria-hidden="true">
            <path d="M2 8 Q 20 2, 40 8 T 80 8 T 120 8 T 160 8 L 178 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none" />
          </svg>

          <p className="font-default text-muted-foreground max-w-2xl mb-8 text-base leading-normal">At MS Biotechnology, we believe every successful partnership begins with a thorough understanding of the product. Our multidisciplinary evaluation process reviews clinical applications, scientific evidence, market potential, and preliminary regulatory considerations to identify technologies with meaningful clinical and commercial value.<div><br /></div><div>By combining healthcare expertise with global market insight, we help manufacturers and partners make informed decisions and build successful international collaborations.</div></p>
          
          
        </div>

        <div ref={cardsRef} className="mt-20 md:mt-28 grid grid-cols-1 md:grid-cols-4 gap-12 md:gap-8 lg:gap-12 relative">
          {steps.map((step, i) => {
          const Icon = step.Icon;
          const isLast = i === steps.length - 1;
          return <div key={i} data-index={i} className={`relative transition-all duration-700 ease-out ${cardsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
            transitionDelay: `${i * 120}ms`
          }}>
                <Card className={`group bg-white border border-border rounded-lg p-7 md:p-8 transform ${step.rotation} ${step.hoverRotation} transition-smooth relative shadow-sm hover:shadow-lg`}>
                  <div className="font-default font-bold leading-none text-primary/20 mb-6" style={{
                fontSize: "clamp(3rem, 5vw, 4rem)"
              }}>
                    {step.number}
                  </div>

                  <div className="flex justify-start mb-6">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-lg bg-primary/5 text-primary">
                      <Icon className="w-8 h-8" strokeWidth={1.5} />
                    </div>
                  </div>

                  <h3 className="font-default text-foreground mb-3 font-semibold text-xl">
                    {step.title}
                  </h3>

                  <p className="font-default text-muted-foreground mb-6 text-base leading-normal">
                    {step.body}
                  </p>

                  
                </Card>

                {!isLast && <svg className="hidden md:block absolute top-1/2 -right-6 lg:-right-8 w-12 lg:w-16 h-12 text-primary/30 pointer-events-none" viewBox="0 0 64 48" fill="none" aria-hidden="true" style={{
              transform: "translateY(-50%)"
            }}>
                    <path d="M2 24 Q 16 14, 30 26 T 56 22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none" />
                    <path d="M56 22 L 48 16 M 56 22 L 50 30" stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none" />
                  </svg>}
              </div>;
        })}
        </div>
      </div>
    </section>;
}